import { useSelector } from 'react-redux';
import { ExpandableList, IconName } from '@optimus-web/core';
import { DateTimeUtcService, useAppClockService, useFeatureFlag } from '@mews/framework';
import { FeatureFlag, TextKey } from '@mews-commander/enums';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { hasCurrentEnterpriseServiceProviderMode } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import { useCommanderSelector } from '@mews-commander/utils';
import { createListItemWithLink } from '../helpers';
import { getPreviousMonthUtc } from '../../dashboard/selectors/tasks';
import { getTaskReportIndexQueryParams } from '../../dashboard/actions/util';
import AnalyticsMenu from './AnalyticsMenu';
import EnterpriseQueueMenu from './EnterpriseQueueMenu';
import ExportsMenu from './ExportsMenu';
import FinanceMenu from './FinanceMenu';
import GlobalizationMenu from './GlobalizationMenu';
import IntegrationMenu from './IntegrationMenu';
import LinkMenu from './LinkMenu';
import { CommonMenuItems } from './MenuItems';
import { OperationMenu } from './OperationMenu';
import PlacesMenu from './PlacesMenu';
import ProfileMenu from './ProfileMenu';
import { QueueMenu } from './QueueMenu';
import ReservationMenu from './ReservationMenu';
import { CapitalMenu } from './CapitalMenu';
import { SettingsMenu } from './SettingsMenu';
import BIMenuItem from './BIMenuItem';
import { AtomizeMenuItem } from './AtomizeMenuItem';

const messagesMenuItem = createListItemWithLink({
    textKey: TextKey.MessagePlural,
    iconName: IconName.Message,
    path: 'MessageThread/Index',
    isMainItem: true,
});

export const PlatformAdminAndServiceProviderMenu = () => {
    const hasServiceProviderMode: boolean = useSelector(hasCurrentEnterpriseServiceProviderMode);
    const currentEnterpriseId = useCurrentEnterpriseId();
    const financialServicesCapitalAccessAuthorized = useFeatureFlag(FeatureFlag.financialServicesCapitalAccess);
    const rmsModuleEnabled = useFeatureFlag(FeatureFlag.rmsModuleEnabled);

    const marketplaceMenuItem = createListItemWithLink({
        textKey: TextKey.Marketplace,
        iconName: IconName.Marketplace,
        path: 'EnterpriseIntegration/Marketplace',
        search: { OwnerId: currentEnterpriseId },
        isMainItem: true,
    });

    return (
        <ExpandableList>
            <QueueMenu value="queue" />

            <ProfileMenu value="profile" />

            <ReservationMenu value="reservation" />
            <OperationMenu value="operation" />
            <FinanceMenu value="finance" />
            <PlacesMenu value="places" />
            <ExportsMenu value="exports" />
            {hasServiceProviderMode && !!currentEnterpriseId && (
                <>
                    <TaskMenuItem />
                    {messagesMenuItem}
                </>
            )}

            <SettingsMenu value="settings" />
            <EnterpriseQueueMenu value="enterpriseQueue" />
            <LinkMenu value="link" />
            <GlobalizationMenu value="globalization" />
            <IntegrationMenu value="integration" />
            {hasServiceProviderMode && !!currentEnterpriseId && (
                <>
                    <BIMenuItem />
                    {rmsModuleEnabled && <AtomizeMenuItem />}
                    <AnalyticsMenu value="analytics" enterpriseId={currentEnterpriseId} />
                    {marketplaceMenuItem}
                    {financialServicesCapitalAccessAuthorized && <CapitalMenu />}
                </>
            )}
            <CommonMenuItems />
        </ExpandableList>
    );
};

const TaskMenuItem = () => {
    const appClockService = useAppClockService();
    const timezone: string = useCommanderSelector(getCurrentEnterpriseIanaTimeZoneIdentifier);

    const nowUtc = DateTimeUtcService.toISOString(appClockService.getAppNowUtc());
    const startUtc = useCommanderSelector((state) => getPreviousMonthUtc(state, { appClockService }));

    return createListItemWithLink({
        textKey: TextKey.TaskPlural,
        iconName: IconName.Tasks,
        path: 'TaskReport/Index',
        isMainItem: true,
        search: getTaskReportIndexQueryParams(timezone, nowUtc, startUtc),
    });
};
